import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Polygon, Line, Circle, Text as SvgText } from 'react-native-svg';
import { ChartConfig } from '../types';

interface RadarChartDataset {
  name: string;
  data: number[];
  color: string;
}

interface RadarChartData {
  name: string;
  value: number;
  color: string;
  legendFontColor: string;
  legendFontSize: number;
}

interface RadarChartProps {
  data: RadarChartData[] | RadarChartDataset[];
  width: number;
  height: number;
  chartConfig: ChartConfig;
  accessor: string;
  backgroundColor?: string;
  paddingLeft?: string;
  center?: [number, number];
  absolute?: boolean;
  hasLegend?: boolean;
  onDataPointClick?: (data: any, index: number) => void;
  isZeroValues?: boolean;
  datasets?: RadarChartDataset[];
  labels?: string[];
  value?: number;
  setChartValue?: (value: string | number) => void;
}

const RadarChart: React.FC<RadarChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  accessor,
  center = [0, 0],
  hasLegend = true,
  onDataPointClick,
  isZeroValues = false,
  datasets,
  labels,
  setChartValue,
}) => {
  const padding = 60;
  const radius = Math.min(width - padding * 2, height - padding * 2) / 2;
  const centerX = width / 2 + center[0];
  const centerY = height / 2 + center[1];


  // Handle both single dataset and multiple datasets
  const isMultiDataset = datasets && datasets.length > 0;
  const axisLabels =
    labels || (Array.isArray(data) ? data.map((item) => item.name) : []);
  const numAxes = axisLabels.length;

  // Calculate max value across all datasets
  let maxValue = 1;
  if (isMultiDataset) {
    maxValue = Math.max(...datasets.flatMap((dataset) => dataset.data), 1);
  } else if (Array.isArray(data)) {
    maxValue = Math.max(
      ...data.map((item) =>
        'value' in item
          ? item.value
          : 'data' in item
          ? Math.max(...item.data)
          : 0
      ),
      1
    );
  }
  const levels = 5; // Number of concentric levels

  // Calculate points for a given dataset
  const getDatasetPoints = (values: number[]) => {
    return values.map((value, index) => {
      const angle = (index / numAxes) * 2 * Math.PI;
      const normalizedValue = isZeroValues
        ? 0.3
        : Math.min(value / maxValue, 1);
      const x =
        centerX + Math.cos(angle - Math.PI / 2) * radius * normalizedValue;
      const y =
        centerY + Math.sin(angle - Math.PI / 2) * radius * normalizedValue;
      return { x, y };
    });
  };

  // Generate spider web grid
  const gridElements = [];

  // 1. Create concentric circles (web rings)
  for (let level = 1; level <= levels; level++) {
    const levelRadius = (radius * level) / levels;
    gridElements.push(
      <Circle
        key={`level-${level}`}
        cx={centerX}
        cy={centerY}
        r={levelRadius}
        fill="none"
        stroke="#9a9a9a"
        strokeWidth={0.5}
        opacity={0.7}
      />
    );
  }

  // 2. Create radial lines (web spokes)
  for (let i = 0; i < numAxes; i++) {
    const angle = (i / numAxes) * 2 * Math.PI;
    const endX = centerX + Math.cos(angle - Math.PI / 2) * radius;
    const endY = centerY + Math.sin(angle - Math.PI / 2) * radius;

    gridElements.push(
      <Line
        key={`axis-${i}`}
        x1={centerX}
        y1={centerY}
        x2={endX}
        y2={endY}
        stroke="#9a9a9a"
        strokeWidth={0.5}
        opacity={0.7}
      />
    );
  }

  // 3. Optional: Add connecting lines between axes
  for (let i = 0; i < numAxes; i++) {
    const angle1 = (i / numAxes) * 2 * Math.PI;
    const angle2 = (((i + 1) % numAxes) / numAxes) * 2 * Math.PI;

    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const x1 = centerX + Math.cos(angle1 - Math.PI / 2) * levelRadius;
      const y1 = centerY + Math.sin(angle1 - Math.PI / 2) * levelRadius;
      const x2 = centerX + Math.cos(angle2 - Math.PI / 2) * levelRadius;
      const y2 = centerY + Math.sin(angle2 - Math.PI / 2) * levelRadius;

      gridElements.push(
        <Line
          key={`connector-${i}-${level}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#9a9a9a"
          strokeWidth={0.3}
          opacity={0.5}
        />
      );
    }
  }

  // Generate data polygons
  const dataPolygons = [];
  let legendItems: any[] = [];

  if (isMultiDataset) {
    // Multiple datasets
    datasets.forEach((dataset, datasetIndex) => {
      const dataPoints = getDatasetPoints(dataset.data);
      const polygonPoints = dataPoints
        .map((point) => `${point.x},${point.y}`)
        .join(' ');

      dataPolygons.push(
        <Polygon
          key={`dataset-${datasetIndex}`}
          points={polygonPoints}
          fill={dataset.color}
          fillOpacity={0.3}
          stroke={dataset.color}
          strokeWidth={2}
          onPress={() => onDataPointClick?.(dataset, datasetIndex)}
        />
      );

      // Add data points
      dataPoints.forEach((point, pointIndex) => {
        dataPolygons.push(
          <Circle
            key={`dataset-${datasetIndex}-point-${pointIndex}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill={dataset.color}
            stroke="#ffffff"
            strokeWidth="2"
            onPress={() =>
              onDataPointClick?.({ dataset, pointIndex }, pointIndex)
            }
          />
        );
      });

      legendItems.push({
        name: dataset.name,
        color: dataset.color,
        legendFontColor: '#666666',
        legendFontSize: 14,
      });
    });
  } else if (Array.isArray(data)) {
    // Single dataset
    const dataPoints =
      Array.isArray(data) && data.length > 0 && 'value' in data[0]
        ? getDatasetPoints(data.map((item: any) => item.value))
        : [];
    const polygonPoints = dataPoints
      .map((point) => `${point.x},${point.y}`)
      .join(' ');

    dataPolygons.push(
      <Polygon
        key="single-dataset"
        points={polygonPoints}
        fill={chartConfig.color?.(0.3) || 'rgba(134, 65, 244, 0.3)'}
        stroke={chartConfig.color?.(0.5) || '#8665F4'}
        strokeWidth={chartConfig.strokeWidth || 2}
        onPress={() => onDataPointClick?.(data, 0)}
      />
    );

    // Add data points
    dataPoints.forEach((point, index) => {
      dataPolygons.push(
        <Circle
          key={`point-${index}`}
          cx={point.x}
          cy={point.y}
          r={chartConfig.propsForDots?.r || '4'}
          fill={data[index].color || chartConfig.color?.(1) || '#8665F4'}
          stroke={chartConfig.propsForDots?.stroke || '#ffffff'}
          strokeWidth={chartConfig.propsForDots?.strokeWidth || '2'}
          onPress={() => onDataPointClick?.(data[index], index)}
        />
      );
    });

    legendItems = data.map((item) => {
      if ('legendFontSize' in item) {
        return {
          name: item.name,
          color: item.color,
          legendFontColor: item.legendFontColor,
          legendFontSize: item.legendFontSize,
        };
      } else {
        return {
          name: item.name,
          color: item.color,
          legendFontColor: '#666666',
          legendFontSize: 14,
        };
      }
    });
  }

  const sumValue = React.useMemo(() => {
    return datasets?.reduce((sum, item) => sum + (item.data?.reduce((sum, item) => sum + item, 0) || 0), 0) || 0;
  }, [datasets]);

  React.useEffect(() => {
    if(setChartValue) {
      setChartValue(sumValue);
    }
  }, [sumValue]);

  return (
    <View style={{ backgroundColor: chartConfig.backgroundColor, height, width }}>
      <Svg width={width} height={height}>
        {/* Spider web grid - rendered first */}
        {gridElements}

        {/* Data polygons and points */}
        {dataPolygons}

        {/* Axis Labels */}
        {axisLabels.map((label, index) => {
          const angle = (index / numAxes) * 2 * Math.PI;
          const labelRadius = radius + 25;
          const labelX = centerX + Math.cos(angle - Math.PI / 2) * labelRadius;
          const labelY = centerY + Math.sin(angle - Math.PI / 2) * labelRadius;

          return (
            <SvgText
              key={`label-${index}`}
              x={labelX}
              y={labelY}
              textAnchor="middle"
              alignmentBaseline="middle"
              fontSize="12"
              fill="#666666"
              fontWeight="bold"
            >
              {label}
            </SvgText>
          );
        })}

        {/* Value labels on grid levels */}
        {Array.from({ length: levels }, (_, level) => {
          const value = Math.round(((level + 1) / levels) * maxValue);
          const levelRadius = (radius * (level + 1)) / levels;
          return (
            <SvgText
              key={`value-${level}`}
              x={centerX + 5}
              y={centerY - levelRadius}
              textAnchor="start"
              alignmentBaseline="middle"
              fontSize="10"
              fill="#999999"
            >
              {value}
            </SvgText>
          );
        })}
      </Svg>

      {hasLegend && legendItems.length > 0 && (
        <View style={{ paddingHorizontal: 20, paddingTop: 10 }}>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {legendItems.map((item, index) => (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 5,
                  marginRight: 15,
                }}
              >
                <View
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: item.color,
                    marginRight: 8,
                    borderRadius: 2,
                  }}
                />
                <Text
                  style={{
                    color: item.legendFontColor,
                    fontSize: item.legendFontSize,
                  }}
                >
                  {item.name}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export default RadarChart;
